import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MultiSelect } from "chakra-multiselect";

// Chakra imports
import {
  Box,
  Button,
  Flex,
  Table,
  TableCaption,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";

// Custom components
import { useLazyQuery } from "@apollo/client";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import RequestRow from "components/Tables/RequestRow";
import SkeletonTable from "components/Tables/SkeletonTable";
import { FETCH_REQUESTS } from "utils/queries";
// import SearchBar from "components/SearchBar/SearchBar";
import {
  requestStatusOptions,
  inputAvailableChains,
  chainLabelToValue,
  DEFAULT_CHAINS,
  DEFAULT_REQUEST_STATUSES as DEFAULT_STATUS,
} from "utils/constants";
import { FETCH_REQUESTS_BY_STATUS } from "utils/queries";
import { FETCH_REQUEST } from "utils/queries";
import { FETCH_REQUESTS_BY_STATUS_AND_REQUEST_ID } from "utils/queries";
import { FETCH_REQUESTS_BY_REQUESTING_CHAIN } from "utils/queries";
import { FETCH_REQUESTS_BY_DESTINATION_CHAIN } from "utils/queries";
import { FETCH_REQUESTS_BY_STATUS_AND_REQUESTING_CHAIN } from "utils/queries";
import { FETCH_REQUESTS_BY_STATUS_AND_DESTINATION_CHAIN } from "utils/queries";
import SortableTh from "components/Tables/SortableTh";
import useSortableTable from "hooks/useSortableTable";

// Constants moved to src/utils/constants.js

const SEARCH_TYPES = {
  REQUEST_ID: "requestId",
  REQUESTING_CHAIN: "requestingChain",
  DESTINATION_CHAIN: "destinationChain",
};

function Requests() {
  const [searchParams, setSearchParams] = useSearchParams();
  const NUM_REQUESTS = 50;
  const [requestingChains, setRequestingChains] = useState([]);
  const [destinationChains, setDestinationChains] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [requests, setRequests] = useState([]);
  const [skip, setSkip] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [searchType, setSearchType] = useState(SEARCH_TYPES.REQUEST_ID); // Added searchType state
  const [getAllRequests] = useLazyQuery(FETCH_REQUESTS);
  const [getAllRequestByStatus] = useLazyQuery(FETCH_REQUESTS_BY_STATUS);
  const [fetchRequestByRequestId] = useLazyQuery(FETCH_REQUEST);
  const [fetchRequestByStatusAndRequestId] = useLazyQuery(
    FETCH_REQUESTS_BY_STATUS_AND_REQUEST_ID
  );
  const [fetchRequestsByRequestingChain] = useLazyQuery(
    FETCH_REQUESTS_BY_REQUESTING_CHAIN
  );
  const [fetchRequestsByDestinationChain] = useLazyQuery(
    FETCH_REQUESTS_BY_DESTINATION_CHAIN
  );
  const [fetchRequestsByStatusAndRequestingChain] = useLazyQuery(
    FETCH_REQUESTS_BY_STATUS_AND_REQUESTING_CHAIN
  );
  const [fetchRequestsByStatusAndDestinationChain] = useLazyQuery(
    FETCH_REQUESTS_BY_STATUS_AND_DESTINATION_CHAIN
  );

  const updateRequestsState = (data, currentSkip) => {
    if (data.requests.length < NUM_REQUESTS) {
      setHasMore(false);
    } else {
      setHasMore(true);
    }
    if (currentSkip === 0) {
      setRequests(data.requests);
    } else {
      setRequests((prevRequests) => [...prevRequests, ...data.requests]);
    }
  };

  const fetchAllRequests = async (currentSkip) => {
    console.log("Fetching requests with filters");
    console.log({
      statusOptions,
      requestingChains,
      destinationChains,
      searchValue,
      currentSkip,
    });
    setIsLoading(true);

    // Base variables for pagination and sorting
    const variables = {
      orderBy: `indexedAt`,
      orderDirection: `desc`,
      first: NUM_REQUESTS,
      skip: currentSkip,
    };

    try {
      // Extract values for filters
      const statusValues = statusOptions.map((status) => status);

      // Only add filters if they have values
      if (statusValues.length > 0) {
        variables.status = statusValues;
      } else {
        variables.status = DEFAULT_STATUS;
      }

      if (requestingChains.length > 0) {
        variables.requestingChainDomain = requestingChains.map(
          (chain) => chainLabelToValue[chain]
        );
      } else {
        variables.requestingChainDomain = DEFAULT_CHAINS;
      }

      if (destinationChains.length > 0) {
        variables.destinationChainDomain = destinationChains.map(
          (chain) => chainLabelToValue[chain]
        );
      } else {
        variables.destinationChainDomain = DEFAULT_CHAINS;
      }

      if (searchValue.trim() !== "") {
        variables.requestId = searchValue.trim();
      } else {
        variables.requestId = "";
      }

      console.log("Querying with variables:", variables);

      // Use a single query with all possible filters
      const data = await getAllRequests({
        variables,
      });

      if (data && data.data) {
        updateRequestsState(data.data, currentSkip);
      }
    } catch (error) {
      console.log("Error fetching requests", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMore = () => {
    const newSkip = skip + NUM_REQUESTS;
    setSkip(newSkip); // Update the skip state
    fetchAllRequests(newSkip); // Fetch the next batch
  };

  // Define available chains for dropdowns

  useEffect(() => {
    // Initialize with empty selections
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setSkip(0);
      fetchAllRequests(0);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchValue, requestingChains, destinationChains, statusOptions]);

  const resetData = () => {
    setSkip(0);
    getAllRequests({
      variables: {
        orderBy: "indexedAt",
        orderDirection: "desc",
        first: NUM_REQUESTS,
        skip: 0,
        requestingChainDomain: DEFAULT_CHAINS,
        destinationChainDomain: DEFAULT_CHAINS,
        status: DEFAULT_STATUS,
      },
    }).then((response) => {
      const { data } = response;
      setRequests(data?.requests);
    });
  };

  const {
    sortedItems: sortedRequests,
    handleSort,
    sortKey,
    sortDirection,
  } = useSortableTable(requests, "indexedAt", "desc");

  const getArrayFromQueryParam = (param) => {
    return param
      ? param
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
      : [];
  };

  // Initialize filters from URL query parameters
  useEffect(() => {
    const urlRequestId = searchParams.get("requestId");
    const urlRequestingChains = searchParams.get("requestingChains");
    const urlDestinationChains = searchParams.get("destinationChains");
    const urlStatus = searchParams.get("status");

    if (urlRequestId) {
      setSearchValue(urlRequestId);
      setSearchType(SEARCH_TYPES.REQUEST_ID); // Assume if requestId is in param, it's the search type
    }
    setRequestingChains(getArrayFromQueryParam(urlRequestingChains));
    setDestinationChains(getArrayFromQueryParam(urlDestinationChains));
    setStatusOptions(getArrayFromQueryParam(urlStatus));

    setIsInitialized(true);
  }, []); // Runs once on mount

  // Refetch data and update URL when filters change
  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    setSkip(0);
    fetchAllRequests(0);

    const currentUrlParams = new URLSearchParams(searchParams.toString());
    const newUrlParams = new URLSearchParams();

    if (searchType === SEARCH_TYPES.REQUEST_ID && searchValue) {
      newUrlParams.set("requestId", searchValue);
    }
    if (requestingChains.length > 0) {
      newUrlParams.set("requestingChains", requestingChains.join(","));
    }
    if (destinationChains.length > 0) {
      newUrlParams.set("destinationChains", destinationChains.join(","));
    }
    if (statusOptions.length > 0) {
      newUrlParams.set("status", statusOptions.join(","));
    }

    if (newUrlParams.toString() !== currentUrlParams.toString()) {
      setSearchParams(newUrlParams, { replace: true });
    }
  }, [
    isInitialized,
    searchValue,
    searchType,
    requestingChains,
    destinationChains,
    statusOptions,
    setSearchParams,
  ]);

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card
        // overflowX={{ sm: "scroll", xl: "hidden" }}
        pb="0px"
        className="scroll-content"
      >
        <CardHeader p="6px 0px 22px 0px" w="95%" flexDirection="column">
          <Text fontSize="lg" color="#fff" fontWeight="bold">
            Requests
          </Text>
          <Flex
            justifyContent="space-between"
            alignItems="flex-start"
            w="100%"
            mt="30px"
            flexDir={{ base: "column", md: "row" }}
            gap={4}
          >
            {/* Search Input for Request ID */}
            <Box flex={1} w={{ base: "100%" }}>
              <FormControl>
                <FormLabel color="gray.400">Search by Request ID</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Enter Request ID"
                    value={searchValue}
                    onChange={(e) => {
                      resetData();
                      setSearchValue(e.target.value);
                    }}
                    variant="filled"
                    bg="gray.700"
                    color="white"
                    _placeholder={{ color: "gray.400" }}
                    _hover={{ bg: "whiteAlpha.200" }}
                    _focus={{ bg: "whiteAlpha.300" }}
                    borderColor="whiteAlpha.200"
                  />
                </InputGroup>
              </FormControl>
            </Box>

            {/* Requesting Chains Dropdown */}
            <Box
              className="custom-multi-select"
              w={{ base: "100%", md: "auto" }}
              sx={{
                ".chakra-multiselect__menu": {
                  bg: "gray.800",
                  borderColor: "whiteAlpha.200",
                  color: "white",
                },
                ".chakra-multiselect__option": {
                  _hover: { bg: "whiteAlpha.200" },
                  "&[data-selected]": {
                    bg: "blue.500",
                  },
                  "&[data-focused]": {
                    bg: "whiteAlpha.200",
                  },
                },
              }}
            >
              <FormControl>
                <FormLabel color="gray.400">Requesting Chains</FormLabel>
                <MultiSelect
                  options={inputAvailableChains}
                  value={requestingChains}
                  onChange={setRequestingChains}
                  placeholder="Select Requesting Chains"
                  multiple
                />
              </FormControl>
            </Box>

            {/* Destination Chains Dropdown */}
            <Box
              className="custom-multi-select"
              w={{ base: "100%", md: "auto" }}
              // minW="180px"
              sx={{
                ".chakra-multiselect__menu": {
                  bg: "gray.800",
                  borderColor: "whiteAlpha.200",
                  color: "white",
                },
                ".chakra-multiselect__option": {
                  _hover: { bg: "whiteAlpha.200" },
                  "&[data-selected]": {
                    bg: "blue.500",
                  },
                  "&[data-focused]": {
                    bg: "whiteAlpha.200",
                  },
                },
              }}
            >
              <FormControl>
                <FormLabel color="gray.400">Destination Chains</FormLabel>
                <MultiSelect
                  options={inputAvailableChains}
                  value={destinationChains}
                  onChange={setDestinationChains}
                  placeholder="Select Destination Chains"
                  multiple
                />
              </FormControl>
            </Box>

            {/* Request Status Dropdown */}
            <Box
              className="custom-multi-select"
              w={{ base: "100%", md: "auto" }}
              sx={{
                ".chakra-multiselect__menu": {
                  bg: "gray.800",
                  borderColor: "whiteAlpha.200",
                  color: "white",
                },
                ".chakra-multiselect__option": {
                  _hover: { bg: "whiteAlpha.200" },
                  "&[data-selected]": {
                    bg: "blue.500",
                  },
                  "&[data-focused]": {
                    bg: "whiteAlpha.200",
                  },
                },
              }}
            >
              <FormControl>
                <FormLabel color="gray.400">Request Status</FormLabel>
                <MultiSelect
                  options={requestStatusOptions}
                  value={statusOptions}
                  onChange={setStatusOptions}
                  placeholder="Select Request Status"
                  multiple
                />
              </FormControl>
            </Box>
          </Flex>
        </CardHeader>
        <CardBody>
          <Table variant="simple" color="#fff">
            <TableCaption cursor="pointer" _hover={{ color: "#fff" }}>
              <Button
                onClick={loadMore}
                isLoading={isLoading}
                isDisabled={!hasMore}
              >
                Load More
              </Button>
            </TableCaption>
            <Thead>
              <Tr my=".8rem" ps="0px" color="gray.400">
                <Th
                  ps="0px"
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Request ID
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Status
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Requesting Chain
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Desitnation Chain
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Value
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  First Committed Epoch
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Finalized Epoch
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Number of Commits
                </Th>
                {/* Use SortableTh for Timestamp */}
                <SortableTh
                  sortKeyName="indexedAt"
                  handleSort={handleSort}
                  currentSortKey={sortKey}
                  currentSortDirection={sortDirection}
                >
                  Timestamp
                </SortableTh>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading && sortedRequests.length === 0 ? (
                <SkeletonTable rowCount={10} columnCount={9} /> // Show skeleton while loading and empty
              ) : !isLoading && sortedRequests.length === 0 ? (
                // Show 'No Data' message when not loading and empty
                <Tr>
                  <Td colSpan={9} textAlign="center">
                    {" "}
                    {/* Adjust colSpan as needed */}
                    No requests found.
                  </Td>
                </Tr>
              ) : (
                sortedRequests.length > 0 &&
                sortedRequests.map((row, index, arr) => {
                  return (
                    <RequestRow
                      row={row}
                      key={index}
                      lastItem={index === arr.length - 1 ? true : false}
                    />
                  );
                })
              )}
            </Tbody>
          </Table>
        </CardBody>
      </Card>
    </Flex>
  );
}

export default Requests;
